import { useMemo, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, X } from "lucide-react"; // 引入 X 图标
import { Button } from "@/components/ui/button";
import { scenarios } from "@/data/scenario-registry";
import ScriptAwarenessGuide from "@/components/ScriptAwarenessGuide";

// ===== Data (保持不变) =====
const SCENARIOS = [
  {
    id: "office",
    title: "场景一：办公室/职场",
    tKey: "lifeEditor.scenarios.office",
    scripts: Object.values(scenarios)
      .filter((s) => s.id.startsWith("office_"))
      .map((s) => ({
        id: s.id,
        title: s.title,
        tKey: s.titleKey,
        image: s.sceneImage,
      })),
  },
  {
    id: "home",
    title: "场景二：家庭/亲密关系",
    tKey: "lifeEditor.scenarios.home",
    scripts: Object.values(scenarios)
      .filter((s) => s.id.startsWith("home_"))
      .map((s) => ({
        id: s.id,
        title: s.title,
        tKey: s.titleKey,
        image: s.sceneImage,
      })),
  },
  {
    id: "parent",
    title: "场景三：亲子/教育",
    tKey: "lifeEditor.scenarios.parent",
    scripts: Object.values(scenarios)
      .filter((s) => s.id.startsWith("parent_"))
      .map((s) => ({
        id: s.id,
        title: s.title,
        tKey: s.titleKey,
        image: s.sceneImage,
      })),
  },
  {
    id: "social",
    title: "场景四：社交/朋友圈",
    tKey: "lifeEditor.scenarios.social",
    scripts: Object.values(scenarios)
      .filter((s) => s.id.startsWith("social_"))
      .map((s) => ({
        id: s.id,
        title: s.title,
        tKey: s.titleKey,
        image: s.sceneImage,
      })),
  },
  {
    id: "inner",
    title: "场景五：独处/内在自我",
    tKey: "lifeEditor.scenarios.inner",
    scripts: Object.values(scenarios)
      .filter((s) => s.id.startsWith("inner_"))
      .map((s) => ({
        id: s.id,
        title: s.title,
        tKey: s.titleKey,
        image: s.sceneImage,
      })),
  },
];

// ===== Helper =====
function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

// ===== Centralized Overlay Card Component (修改后的核心组件) =====
// 这个组件现在是一个全屏的 Modal，居中显示
function CentralScriptCard({
  title,
  id,
  image,
  onClose,
}: {
  title: string;
  id?: string;
  image?: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const go = () => navigate(`/life-editor/play/${id ?? encodeURIComponent(title)}`);

  const imgSrc = image || (id ? `/scenarios/${id}.jpg` : undefined);

  // 点击背景关闭
  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className={cn(
          "relative w-full max-w-sm md:max-w-4xl aspect-[3/4] md:aspect-video rounded-xl overflow-hidden shadow-2xl",
          "transform transition-all duration-300 scale-100", // 初始状态
          "cursor-pointer group ring-4 ring-white/20"
        )}
        onClick={(e) => {
          e.stopPropagation(); // 防止点击卡片触发背景关闭
          go();
        }}
      >
        {/* 关闭按钮 (手机端体验更好) */}
        <button 
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="absolute top-2 right-2 z-50 p-2 rounded-full bg-black/50 text-white hover:bg-black/70"
        >
          <X size={20} />
        </button>

        {imgSrc ? (
          <img src={imgSrc} alt={title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-purple-100 via-indigo-100 to-pink-100 flex items-center justify-center">
             <span className="text-gray-800 text-lg font-medium">{t("comingSoon.badge")}</span>
          </div>
        )}
        
        {/* 底部文字遮罩 */}
        <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/90 via-black/60 to-transparent pt-20 pb-6 px-6">
          <h3 className="text-white text-xl md:text-2xl font-bold leading-tight mb-2">
            {title}
          </h3>
          <p className="text-white/80 text-sm flex items-center gap-1">
            {t("Tap to start")} <ArrowRight size={14} />
          </p>
        </div>
      </div>
    </div>
  );
}

// ===== Scenario Section (列表部分) =====
function ScenarioSection({
  scenario,
  onSelectScript, 
}: {
  scenario: { id: string; title: string; tKey?: string; icon?: string; scripts: { title: string; tKey?: string; id?: string; image?: string }[] };
  onSelectScript: (script: any) => void;
}) {
  const { t } = useTranslation();
  const safeT = (key?: string, fallback?: string) => {
    if (!key) return fallback ?? "";
    const val = t(key);
    return val === key ? (fallback ?? key) : val;
  };
  
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const { current } = scrollContainerRef;
      const scrollAmount = current.offsetWidth * 0.8;
      current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="relative rounded-xl border bg-card text-card-foreground shadow p-4 md:p-6 mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl md:text-2xl font-semibold flex items-center text-primary">
          {safeT(scenario.tKey, scenario.title)}
        </h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => scroll("left")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => scroll("right")}>
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div
        ref={scrollContainerRef}
        className="flex -mx-2 overflow-x-auto pb-4 scrollbar-hide scrolling-wrapper snap-x"
      >
        {scenario.scripts.map((script, index) => (
          <div
            key={index}
            className="flex-none w-72 md:w-80 px-2 snap-start cursor-pointer transition-transform hover:scale-95 active:scale-95"
            onClick={() => onSelectScript(script)} // 点击触发全屏展示
          >
            <div className={cn(
              "relative w-full aspect-video rounded-lg overflow-hidden border border-purple-100 shadow-sm",
              (script.id || script.image) ? "" : "bg-gradient-to-br from-purple-50 via-indigo-50 to-pink-50"
            )}>
              {script.id || script.image ? (
                <img 
                  src={script.id ? `/scenarios/${script.id}.jpg` : script.image} 
                  alt={safeT(script.tKey, script.title)} 
                  className="absolute inset-0 w-full h-full object-cover" 
                  loading="lazy"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-gray-400 text-xs font-medium">{t("comingSoon.badge")}</span>
                </div>
              )}
              
              {/* 列表中的小标题 */}
              <div className="absolute bottom-0 left-0 w-full bg-black/60 backdrop-blur-[2px] p-2 md:p-3">
                 <p className="text-white text-xs md:text-sm font-medium line-clamp-2">
                    {safeT(script.tKey, script.title)}
                 </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===== Main Page =====
export default function LifeEditorPage() {
  const { t } = useTranslation();
  // 状态提升：当前选中的卡片信息放在最外层
  const [selectedScript, setSelectedScript] = useState<{
    title: string;
    tKey?: string;
    id?: string;
    image?: string;
  } | null>(null);

  const safeT = (key?: string, fallback?: string) => {
    if (!key) return fallback ?? "";
    const val = t(key);
    return val === key ? (fallback ?? key) : val;
  };

  return (
    <div className="container mx-auto px-4 py-8 pb-24">
      <div className="text-center mb-8">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-primary">{t("lifeEditor.title")}</h1>
        <p className="text-muted-foreground mt-2 text-sm md:text-base">{t("lifeEditor.subtitle")}</p>
        <div className="flex justify-center md:justify-end mt-4">
          <ScriptAwarenessGuide />
        </div>
      </div>

      <div className="space-y-6">
        {SCENARIOS.map((sc) => (
          <ScenarioSection
            key={sc.id}
            scenario={sc}
            onSelectScript={setSelectedScript}
          />
        ))}
      </div>

      {/* 如果有选中的卡片，显示全屏居中弹窗 */}
      {selectedScript && (
        <CentralScriptCard
          title={safeT(selectedScript.tKey, selectedScript.title)}
          id={selectedScript.id}
          image={selectedScript.image}
          onClose={() => setSelectedScript(null)}
        />
      )}
    </div>
  );
}